// ============================================================
// CineSync — YouTube Player (Official IFrame API Integration)
// ============================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { loadYouTubeIFrameAPI } from '@/lib/youtube';
import { usePlayerStore } from '@/store/playerStore';
import { useRoomStore } from '@/store/roomStore';
import { ReactionOverlay } from './ReactionOverlay';
import { Loader2, AlertCircle, Play, Pause, RotateCcw, Volume2, VolumeX, Maximize, Minimize } from 'lucide-react';
import { formatTime, clamp } from '@/lib/utils';

interface YouTubePlayerProps {
  videoId: string;
  isHost: boolean;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (time: number) => void;
  onSpeedChange?: (speed: number) => void;
  onEnded?: () => void;
  onOpenSubtitleSearch?: () => void;
}

const HARD_SEEK_THRESHOLD = 2.0; // seconds of drift before reconciling

export function YouTubePlayer({
  videoId,
  isHost,
  onPlay,
  onPause,
  onSeek,
  onSpeedChange,
  onEnded,
  onOpenSubtitleSearch,
}: YouTubePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const containerId = useRef(`yt-player-${Math.random().toString(36).substring(2, 9)}`);

  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLocalBuffering, setIsLocalBuffering] = useState(false);

  const { room } = useRoomStore();
  const {
    volume,
    muted,
    playbackRate,
    subtitleEnabled,
    activeSubtitle,
    setPlaying,
    setCurrentTime,
    setDuration,
    setVolume,
    setMuted,
    setFullscreen,
    isFullscreen,
    lockSync,
    isSyncLocked,
  } = usePlayerStore();

  const prevRoomStateRef = useRef<string | null>(null);
  const lastTimeRef = useRef(0);

  // ── 1. Initialize YouTube Player ─────────────────────────────
  useEffect(() => {
    let isCancelled = false;
    setErrorMessage(null);
    setIsPlayerReady(false);

    loadYouTubeIFrameAPI()
      .then((YT) => {
        if (isCancelled || !document.getElementById(containerId.current)) return;

        // Destroy previous instance if any
        if (playerRef.current?.destroy) {
          try {
            playerRef.current.destroy();
          } catch { }
        }

        const initialTime = room?.currentTime || 0;

        playerRef.current = new YT.Player(containerId.current, {
          width: '100%',
          height: '100%',
          videoId,
          playerVars: {
            autoplay: room?.playbackState === 'playing' ? 1 : 0,
            controls: 1, // Full interactive controls for everyone in the room
            disablekb: 0,
            enablejsapi: 1,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            origin: window.location.origin,
            start: Math.floor(initialTime),
            playsinline: 1,
          },
          events: {
            onReady: (event: any) => {
              if (isCancelled) return;
              setIsPlayerReady(true);
              const duration = event.target.getDuration() || 0;
              setDuration(duration);

              // Apply audio preferences
              if (muted) event.target.mute();
              else event.target.unMute();
              event.target.setVolume(Math.round(volume * 100));

              // Seek to room state if late joiner
              if (initialTime > 0) {
                event.target.seekTo(initialTime, true);
              }
              if (room?.playbackState === 'playing') {
                event.target.playVideo();
              } else {
                event.target.pauseVideo();
              }
            },
            onStateChange: (event: any) => {
              if (isCancelled) return;
              const state = event.data;
              const YT = window.YT;

              if (state === YT.PlayerState.BUFFERING) {
                setIsLocalBuffering(true);
              } else {
                setIsLocalBuffering(false);
              }

              if (state === YT.PlayerState.ENDED) {
                setPlaying(false);
                onEnded?.();
                return;
              }

              // Anyone in the room can collaboratively emit play/pause commands to sync everyone
              if (!isSyncLocked()) {
                const exactTime = typeof event.target.getCurrentTime === 'function' ? event.target.getCurrentTime() : 0;
                setCurrentTime(exactTime);
                lastTimeRef.current = exactTime;

                if (state === YT.PlayerState.PLAYING) {
                  setPlaying(true);
                  onPlay();
                } else if (state === YT.PlayerState.PAUSED) {
                  setPlaying(false);
                  onPause();
                }
              }
            },
            onError: (event: any) => {
              const code = event.data;
              console.warn('[YouTube Player] Error code:', code);
              if (code === 101 || code === 150) {
                setErrorMessage('This video cannot be played in an embedded player due to YouTube copyright/owner restrictions.');
              } else if (code === 100) {
                setErrorMessage('The requested YouTube video was not found or is private.');
              } else if (code === 2) {
                setErrorMessage('Invalid YouTube video URL or ID parameter.');
              } else {
                setErrorMessage('YouTube playback error. Please try another video or return home.');
              }
            },
          },
        });
      })
      .catch((err) => {
        if (!isCancelled) {
          setErrorMessage(err.message || 'Failed to load the YouTube IFrame API.');
        }
      });

    return () => {
      isCancelled = true;
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch { }
      }
    };
  }, [videoId, isHost]);

  // ── 2. Time Polling & Scrubbing Detection ────────────────────
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return;

    const interval = setInterval(() => {
      try {
        const player = playerRef.current;
        if (!player || typeof player.getCurrentTime !== 'function') return;

        const time = player.getCurrentTime() || 0;
        setCurrentTime(time);

        // Collaborative seek detection: if time jumped unexpectedly by > 1.5s
        if (!isSyncLocked()) {
          const delta = Math.abs(time - lastTimeRef.current);
          if (delta > 1.5 && lastTimeRef.current > 0) {
            onSeek(time);
          }
        }
        lastTimeRef.current = time;
      } catch { }
    }, 500);

    return () => clearInterval(interval);
  }, [isPlayerReady, onSeek, setCurrentTime, isSyncLocked]);

  // ── 3. Apply Remote Sync State from Room ──────────────────────
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current || !room) return;

    // For participants (and reconciling host reconnects):
    const stateKey = `${room.playbackState}-${room.version}`;
    const player = playerRef.current;

    if (prevRoomStateRef.current !== stateKey) {
      prevRoomStateRef.current = stateKey;

      const localTime = player.getCurrentTime ? player.getCurrentTime() : 0;
      const drift = Math.abs(localTime - room.currentTime);
      const isSeeking = room.playbackState === 'paused' ? drift > 0.6 : drift > HARD_SEEK_THRESHOLD;

      if (isSeeking) {
        lockSync(1000); // Guard against sync feedback loops
        lastTimeRef.current = room.currentTime;
        player.seekTo?.(room.currentTime, true);
      }

      if (room.playbackState === 'playing') {
        const curState = player.getPlayerState?.();
        if (curState !== window.YT?.PlayerState?.PLAYING && curState !== window.YT?.PlayerState?.BUFFERING) {
          player.playVideo?.();
        }
        setPlaying(true);
      } else if (room.playbackState === 'paused') {
        const curState = player.getPlayerState?.();
        if (curState !== window.YT?.PlayerState?.PAUSED) {
          player.pauseVideo?.();
        }
        setPlaying(false);
      }
    }
  }, [room, isPlayerReady, lockSync, setPlaying]);

  // ── 4. Audio & Volume Controls ───────────────────────────────
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !isPlayerReady) return;

    try {
      if (muted) {
        player.mute?.();
      } else {
        player.unMute?.();
      }
      player.setVolume?.(Math.round(volume * 100));
    } catch { }
  }, [volume, muted, isPlayerReady]);

  // ── 5. Fullscreen Handler ───────────────────────────────────
  const handleFullscreen = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      try {
        await container.requestFullscreen();
        setFullscreen(true);
      } catch { }
    } else {
      try {
        await document.exitFullscreen();
        setFullscreen(false);
      } catch { }
    }
  }, [setFullscreen]);

  return (
    <div
      ref={containerRef}
      className="youtube-player-container always-white dark w-full h-full bg-black relative select-none flex items-center justify-center overflow-hidden"
    >
      {/* Target Container for YouTube IFrame */}
      <div className="w-full h-full flex items-center justify-center">
        <div id={containerId.current} className="w-full h-full pointer-events-auto" />
      </div>

      {/* Error Overlay */}
      {errorMessage && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/90 p-6 text-center backdrop-blur-sm animate-fade-in">
          <div className="max-w-md bg-cinema-900 border border-cinema-700/80 p-7 rounded-xl shadow-2xl">
            <AlertCircle className="w-10 h-10 text-reel-rose mx-auto mb-3.5" />
            <h3 className="text-base font-bold text-white mb-2">Embedding Restricted</h3>
            <p className="text-xs text-cinema-300 leading-relaxed mb-5">
              {errorMessage}
            </p>
            <div className="text-[11px] text-cinema-400 p-2.5 rounded bg-cinema-950 border border-cinema-800">
              The room host can switch videos using the <strong>Change Source</strong> button in the room header.
            </div>
          </div>
        </div>
      )}

      {/* Buffering Indicator */}
      {(!isPlayerReady || isLocalBuffering) && !errorMessage && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20 bg-black/40 backdrop-blur-[2px]">
          <div className="bg-black/80 border border-cinema-700 p-3.5 rounded-xl flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 text-lamp animate-spin" />
            <span className="text-xs font-mono text-cinema-200 uppercase tracking-wider">
              {!isPlayerReady ? 'Loading YouTube Video…' : 'Buffering Stream…'}
            </span>
          </div>
        </div>
      )}

      {/* Floating Reactions Overlay */}
      <ReactionOverlay />

      {/* Subtitles Overlay (for custom/OpenSubtitles text when available) */}
      {subtitleEnabled && activeSubtitle && (
        <div className="absolute bottom-16 left-0 right-0 flex justify-center pointer-events-none px-8 z-20">
          <div className="cinema-subtitle bg-black/85 text-white text-base sm:text-lg font-medium px-4 py-1.5 rounded text-center max-w-[85%] leading-relaxed border border-white/10 shadow-lg">
            {activeSubtitle}
          </div>
        </div>
      )}

      {/* Participant Controls Bar (when player has controls disabled) */}
      {!isHost && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-center justify-between z-20 opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <div className="flex items-center gap-2 text-xs text-cinema-300">
            <span className="w-2 h-2 rounded-full bg-lamp animate-pulse" />
            <span className="font-mono text-[11px]">Synced with Host</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setMuted(!muted)}
              className="p-1.5 rounded text-cinema-300 hover:text-white hover:bg-cinema-800 transition-colors"
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <VolumeX className="w-4 h-4 text-reel-rose" /> : <Volume2 className="w-4 h-4" />}
            </button>

            <button
              onClick={handleFullscreen}
              className="p-1.5 rounded text-cinema-300 hover:text-white hover:bg-cinema-800 transition-colors"
              title="Fullscreen"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

